import { z } from 'zod';

const haveSameKeys = (a: object, b: object): boolean => {
  const aKeys = Object.keys(a).sort();
  const bKeys = Object.keys(b).sort();
  return JSON.stringify(aKeys) === JSON.stringify(bKeys);
};

const filterSchema = (s: z.ZodTypeAny) =>
  z.array(z.any()).transform((as) => as.filter((a) => s.safeParse(a).success));

const downloadCSV = (csvContent: string, fileName: string) => {
  // Fix encoding
  const BOM = '\uFEFF';
  const csvWithBOM = BOM + csvContent;

  // Trigger download
  const blob = new Blob([csvWithBOM], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  link.setAttribute('href', url);
  link.setAttribute('download', fileName);
  link.style.visibility = 'hidden';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  URL.revokeObjectURL(url);
};

/**
 * Helper to parse discounts to numbers from deals
 * @param discount the discount in string format
 * @returns number
 */
const parseDiscountValue = (discount: string): number => {
  if (!discount) return 0;

  // Extract percentage values (e.g., "bis zu -32%" → 32, "15%" → 15)
  const percentageMatch = discount.match(/-?(\d+(?:\.\d+)?)%/);
  if (percentageMatch) {
    return parseFloat(percentageMatch[1]);
  }

  // Extract currency values (e.g., "€ 130,-" → 130, "€50" → 50)
  const currencyMatch = discount.match(/[€$£¥]?\s*(\d+(?:[,.]\d+)?)/);
  if (currencyMatch) {
    // Replace comma with dot for proper number parsing
    const numStr = currencyMatch[1].replace(',', '.');
    return parseFloat(numStr);
  }

  // Extract any numbers as fallback
  const numberMatch = discount.match(/(\d+(?:[,.]\d+)?)/);
  if (numberMatch) {
    const numStr = numberMatch[1].replace(',', '.');
    return parseFloat(numStr);
  }

  return 0;
};

const getFrontendUrl = () => {
  if (typeof window === 'undefined') return '';
  return window.location.origin;
};

const getSubdomainAndTldFromHost = (host: string): { subdomain: string; tld: string } => {
  const withoutPort = host.split(':')[0];
  const parts = withoutPort.split('.').filter(Boolean);

  if (parts.length < 3) {
    return { subdomain: '', tld: '' };
  }

  const subdomain = parts[0] === 'www' ? parts[1] : parts[0];
  const lastPart = parts[parts.length - 1] ?? '';
  const secondLastPart = parts[parts.length - 2] ?? '';

  // For local dev with hosts like my.corplife.at.local use 'at' / 'de' as TLD
  const tld = lastPart === 'local' && secondLastPart.length === 2 ? secondLastPart : lastPart;

  return { subdomain, tld };
};

/**
 * Builds a URL with only subdomain and TLD replaced, keeping protocol, port, path and search.
 * Derives the domain (e.g. "corplife") from the current host so it works in any environment.
 * Preserves a trailing ".local" on the hostname for local dev (e.g. my.corplife.at.local).
 */
const urlWithSubdomainAndTld = (baseUrl: string, subdomain: string, tld: string): string => {
  const url = new URL(baseUrl);
  const hostname = url.hostname;
  const parts = hostname.split('.').filter(Boolean);

  if (parts.length < 3) {
    return url.toString();
  }

  const isLocal = hostname.endsWith('.local');
  const domain = isLocal
    ? parts.slice(1, -2).join('.') // e.g. my.corplife.at.local -> corplife
    : parts.slice(1, -1).join('.');
  const localSuffix = isLocal ? '.local' : '';
  url.hostname = `${subdomain}.${domain}.${tld}${localSuffix}`;
  return url.toString();
};

export {
  haveSameKeys,
  filterSchema,
  downloadCSV,
  parseDiscountValue,
  getFrontendUrl,
  getSubdomainAndTldFromHost,
  urlWithSubdomainAndTld,
};
